import controller from '@app/controller.js';

/**
 * @typedef {{
 *   actions: {
 *     onEnter(payload?: MachinePayload): void,
 *     onExit(payload?: MachinePayload): void
 *   },
 *   transitions: {
 *     [key: string]: {
 *       target: string,
 *       action(payload?: MachinePayload): void
 *     }
 *   }
 * }} StateDefinition
 */

/**
 * @typedef {{initialState: string, states: Record<string, StateDefinition>}} MachineDefinition
 */

/**
 * @param {MachineDefinition} stateMachineDefinition
 * @returns {StateMachine}
 */
function createMachine(stateMachineDefinition) {
  const machine = {
    value: stateMachineDefinition.initialState,
    /**
     * Makes a state transition depending on event.
     * @param {string} currentState
     * @param {string} event
     * @param {MachinePayload} payload
     * @returns {string | void}
     */
    transition(currentState, event, payload) {
      const currentStateDefinition =
        stateMachineDefinition.states[currentState];
      const destinationTransition = currentStateDefinition.transitions[event];

      if (!destinationTransition) return;

      const destinationState = destinationTransition.target;
      const destinationStateDefinition =
        stateMachineDefinition.states[destinationState];

      destinationTransition.action(payload);
      currentStateDefinition.actions.onExit(payload);
      machine.value = destinationState;
      destinationStateDefinition.actions.onEnter(payload);

      return machine.value;
    },
  };
  return machine;
}
/** @type {MachineDefinition} */
const nonogramsDefinition = {
  initialState: 'idle',
  states: {
    idle: {
      actions: {
        onEnter() {},
        onExit() {},
      },
      transitions: {
        game: {
          target: 'game',
          action() {},
        },
        reset: {
          target: 'reset',
          action() {},
        },
        save: {
          target: 'save',
          action() {},
        },
        continue: {
          target: 'continue',
          action() {},
        },
        random: {
          target: 'random',
          action() {},
        },
        solution: {
          target: 'solution',
          action() {},
        },
        change: {
          target: 'change',
          action() {},
        },
        results: {
          target: 'results',
          action() {},
        },
      },
    },
    game: {
      actions: {
        onEnter() {
          controller.timer.startTimer();
        },
        onExit() {},
      },
      transitions: {
        idle: {
          target: 'idle',
          action() {},
        },
        draw: {
          target: 'draw',
          action() {},
        },
        reset: {
          target: 'reset',
          action() {},
        },
        save: {
          target: 'save',
          action() {},
        },
        continue: {
          target: 'continue',
          action() {},
        },
        random: {
          target: 'random',
          action() {},
        },
        solution: {
          target: 'solution',
          action() {},
        },
        change: {
          target: 'change',
          action() {},
        },
        results: {
          target: 'results',
          action() {},
        },
      },
    },
    save: {
      actions: {
        onEnter(payload) {
          const returnEvent = payload?.transitionFromState
            ? payload.transitionFromState
            : 'idle';
          if (returnEvent === 'solution' || returnEvent === 'win') {
            controller.showDisallowSave(returnEvent);
          } else {
            controller.saveCurrentSolving(returnEvent);
          }
        },
        onExit() {},
      },
      transitions: {
        game: {
          target: 'game',
          action() {},
        },
        idle: {
          target: 'idle',
          action() {},
        },
        solution: {
          target: 'solution',
          action() {},
        },
        win: {
          target: 'win',
          action() {},
        },
      },
    },
    change: {
      actions: {
        onEnter(payload) {
          const returnEvent = payload?.transitionFromState
            ? payload.transitionFromState
            : 'idle';

          controller.showChangeNonogram(returnEvent);
        },
        onExit() {},
      },
      transitions: {
        idle: {
          target: 'idle',
          action() {},
        },
        game: {
          target: 'game',
          action() {},
        },
        win: {
          target: 'win',
          action() {},
        },
        solution: {
          target: 'solution',
          action() {},
        },
      },
    },
    random: {
      actions: {
        onEnter() {
          controller.getRandomNonogram();
        },
        onExit() {},
      },
      transitions: {
        idle: {
          target: 'idle',
          action() {},
        },
      },
    },
    reset: {
      actions: {
        onEnter() {
          controller.resetNonogram();
        },
        onExit() {},
      },
      transitions: {
        idle: {
          target: 'idle',
          action() {},
        },
      },
    },
    win: {
      actions: {
        onEnter() {
          controller.processWin();
        },
        onExit() {},
      },
      transitions: {
        reset: {
          target: 'reset',
          action() {},
        },
        continue: {
          target: 'continue',
          action() {},
        },
        random: {
          target: 'random',
          action() {},
        },
        change: {
          target: 'change',
          action() {},
        },
        idle: {
          target: 'idle',
          action() {},
        },
        results: {
          target: 'results',
          action() {},
        },
        save: {
          target: 'save',
          action() {},
        },
      },
    },
    continue: {
      actions: {
        onEnter(payload) {
          const returnEvent = payload?.transitionFromState
            ? payload.transitionFromState
            : 'idle';

          controller.continueWithLoadedSolving(returnEvent);
        },
        onExit() {},
      },
      transitions: {
        idle: {
          target: 'idle',
          action() {},
        },
        win: {
          target: 'win',
          action() {},
        },
        solution: {
          target: 'solution',
          action() {},
        },
        game: {
          target: 'game',
          action() {},
        },
      },
    },
    solution: {
      actions: {
        onEnter() {
          controller.showSolution();
        },
        onExit() {},
      },
      transitions: {
        reset: {
          target: 'reset',
          action() {},
        },
        continue: {
          target: 'continue',
          action() {},
        },
        random: {
          target: 'random',
          action() {},
        },
        change: {
          target: 'change',
          action() {},
        },
        idle: {
          target: 'idle',
          action() {},
        },
        results: {
          target: 'results',
          action() {},
        },
        save: {
          target: 'save',
          action() {},
        },
      },
    },
    results: {
      actions: {
        onEnter(payload) {
          const returnEvent = payload?.transitionFromState
            ? payload.transitionFromState
            : 'idle';

          controller.showResults(returnEvent);
        },
        onExit() {},
      },
      transitions: {
        idle: {
          target: 'idle',
          action() {},
        },
        game: {
          target: 'game',
          action() {},
        },
        win: {
          target: 'win',
          action() {},
        },
        solution: {
          target: 'solution',
          action() {},
        },
      },
    },
    draw: {
      actions: {
        onEnter(payload) {
          if (!payload?.cell) {
            machine.transition('draw', 'game');
            return;
          }
          const { x, y, value } = payload.cell;
          controller.processNonogramClicks({ x, y, value });
        },
        onExit() {},
      },
      transitions: {
        game: {
          target: 'game',
          action() {},
        },
        win: {
          target: 'win',
          action() {},
        },
      },
    },
  },
};

const machine = createMachine(nonogramsDefinition);

export default machine;
